/** How long a request for a batch of ops may take before it is given up as a network error. */
export const SEND_TIMEOUT_MS = 30 * 1000;

export const TIMED_OUT = 'The server took too long to answer.';

/**
 * `promise`, or a rejection with TIMED_OUT once `ms` have passed without it settling, calling
 * `onTimeout` first. The sender lock and the queue's barrier wait on a request, so one that stalls
 * (a connection that stops passing traffic without dropping) must end, or nothing else is sent.
 */
export function withTimeout(promise, ms, {
	onTimeout = () => {},
	schedule = (callback, delay) => setTimeout(callback, delay),
	cancel = (timer) => clearTimeout(timer),
} = {}) {
	let timer = null;
	const timeout = new Promise((resolve, reject) => {
		timer = schedule(() => {
			timer = null;
			try {
				onTimeout();
			} catch(error) {
				console.error(error);
			}
			const error = new Error(TIMED_OUT);
			error.timedOut = true;
			reject(error);
		}, ms);
	});
	const settle = () => {
		if(timer !== null) {
			cancel(timer);
			timer = null;
		}
	};
	return Promise.race([promise, timeout]).then((value) => {
		settle();
		return value;
	}, (error) => {
		settle();
		throw error;
	});
}

/**
 * The op queue's `send` over fetch, for the page and the service worker alike: POSTs a budget's ops
 * to the endpoint `endpoints` names for their namespace, as `budget_id=<id>&ops=<JSON string>` (a
 * form post drops empty arrays and stringifies scalars, so the ops go as one JSON string), and
 * `user_id`, whose ops they are, when the queue says: the server refuses them with a 401 when someone
 * else is signed in.
 *
 * Resolves `{ status, body }` for any answer the server gave, the body parsed when it is JSON, and
 * rejects only when no answer came: the queue tells the two apart. An answer that has not come in
 * `timeoutMs` is aborted and rejects as no answer, so the queue backs off.
 *
 * @param {Object} options
 * @param {Object<string, string>} options.endpoints namespace => api action, such as 'food/ops'
 * @param {string|Function} options.baseUrl the api's root, such as 'https://example.com/api/v1', or a function giving it
 */
export function createFetchSender({
	endpoints,
	baseUrl,
	fetchImpl = (...args) => fetch(...args),
	timeoutMs = SEND_TIMEOUT_MS,
	schedule,
	cancel,
}) {
	return (namespace, budgetId, ops, { userId = null } = {}) => {
		const endpoint = endpoints[namespace];
		if(!endpoint) {
			return Promise.resolve({ status: 400, body: { error: `Nothing takes ${namespace} changes.` } });
		}
		const root = String(typeof baseUrl === 'function' ? baseUrl() : baseUrl).replace(/\/+$/, '');
		const body = new URLSearchParams();
		body.set('budget_id', String(budgetId));
		body.set('ops', JSON.stringify(ops));
		if(userId !== null && userId !== undefined) {
			body.set('user_id', String(userId));
		}
		const controller = typeof AbortController === 'function' ? new AbortController() : null;
		const request = fetchImpl(`${root}/${endpoint}`, {
			method: 'POST',
			credentials: 'same-origin',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
			},
			body: body.toString(),
			...(controller ? { signal: controller.signal } : {}),
		}).then((response) => response.json().catch(() => null).then((json) => ({ status: response.status, body: json })));
		return withTimeout(request, timeoutMs, {
			onTimeout: () => (controller ? controller.abort() : undefined),
			...(schedule ? { schedule } : {}),
			...(cancel ? { cancel } : {}),
		});
	};
}
